<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import { get } from 'svelte/store';
  import { GetCachedToken, me } from "@frontend/user";
  import { service } from '@frontend/services';
  import { formatMoney, formatDate, formatDateTime } from './Formats';
  import { push } from "svelte-spa-router";
  import { 
    type AuthenticatedRequestOfDataRequestCashier,
    type ApiResultOfDataResponseCashier,
    type DataRequestCashier,
    type DataResponseCashier,
    type User,
    type PublicUserDTO,
    UserRole
  } from "@frontend/walletapi";
  import { translate, currentLanguage } from "../Translator";
  import { usersByRole, RefreshUsers } from "@frontend/userlist";
  import { groups, RefreshGroups } from "@frontend/grouplist";
  
  let reportData: DataResponseCashier | null = null;
  let error: string = '';
  

  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  let startDate: string = todayString;
  let endDate: string = todayString;
  
  let loading: boolean = false;
  let cashierId: number | null = null;
  let isInitialLoad: boolean = true;


  let allCashiers: User[] = [];
  let searchCashiers: User[] = [];
  let searchInput: string = "";
  let selectedCashier: User | null = null;
  let showAllCashiers: boolean = false;

  interface CashierReport {
    cashier: User;
    reportData: DataResponseCashier | null;
    loading: boolean;
    error: string;
  }
  
  let allCashierReports: CashierReport[] = [];
  let loadingAllReports: boolean = false;
  let cashiersLoaded: boolean = false;

  onMount(async () => {
    if (!$me) {
      push("/user/Login");
      return;
    }
    
    if ($me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin) {
      await RefreshGroups();
      await RefreshUsers(); 
      loadCashiers();
      

      setTimeout(async () => {
        if (!cashiersLoaded && allCashiers.length === 0) {
          await loadCashiersDirectly();
        }
      }, 1000);
      
      await loadAllCashierReports();
    } else {
      if ($me?.Id) {
        cashierId = $me.Id;
      }
      
      await loadCashierReport();
    }
    
    isInitialLoad = false;
  });

  async function loadCashierReport() {
    if (!startDate || !endDate) {
      error = 'Plese select both dates';
      return;
    }

    loading = true;
    error = '';

    try {
      const token = await GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');

      const request: AuthenticatedRequestOfDataRequestCashier = {
        Token: token,
        Data: {
          Datestart: start,
          Dateend: end,
          OperatorId: selectedCashier?.Id || $me?.Id || undefined
        }
      };

      const response: ApiResultOfDataResponseCashier = await service.walletapi.transactionbycashier(request);
      
      if (response.Success && response.Data) {
        reportData = response.Data;
      } else {
        error = response.ErrorMessage || "Error loading report";
        reportData = null;
      }
    } catch (err) {
      error = "Error loading report";
      console.error("Error loading cashier report:", err);
      reportData = null;
    } finally {
      loading = false;
    }
  }

  async function loadAllCashierReports() {
    if (!startDate || !endDate || allCashiers.length === 0) {
      return;
    }
    loadingAllReports = true;
    allCashierReports = [];

    try {
      const token = await GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');

      allCashierReports = allCashiers.map(cashier => ({
        cashier,
        reportData: null,
        loading: true,
        error: ''
      }));

      const reportPromises = allCashiers.map(async (cashier, index) => {
        try {
          const request: AuthenticatedRequestOfDataRequestCashier = {
            Token: token,
            Data: {
              Datestart: start,
              Dateend: end,
              OperatorId: cashier.Id
            }
          };

          const response: ApiResultOfDataResponseCashier = await service.walletapi.transactionbycashier(request);
          
          if (response.Success && response.Data) {
            allCashierReports[index].reportData = response.Data;
            allCashierReports[index].error = '';
          } else {
            allCashierReports[index].error = response.ErrorMessage || "Error loading report";
          }
        } catch (err) {
          allCashierReports[index].error = "Error loading report";
          console.error(`Error loading report for cashier ${cashier.Id}:`, err);
        } finally {
          allCashierReports[index].loading = false;
        }
      });

      await Promise.all(reportPromises);
      
      allCashierReports = [...allCashierReports];
      
    } catch (err) {
      error = "Error loading all reports";
      console.error("Error loading all cashier reports:", err);
    } finally {
      loadingAllReports = false;
    }
  }




  function getOperatorName(): string {
    if (!$me) return 'Cashier current';
    const firstName = $me.FirstName || '';
    const lastName = $me.LastName || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return `Cashier ${$me.Id}`;
    }
  }

  async function handleDateChange() {
    if (!isInitialLoad && startDate && endDate) {
      if ($me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin) {
        await loadAllCashierReports();
      } else {
      await loadCashierReport();
      }
    }
  }


  function loadCashiers() {
    usersByRole[UserRole.Cashier].subscribe(async (cashiers) => {
      
      if ($me?.Role === UserRole.Owner) {
        allCashiers = cashiers;
      } else if ($me?.Role === UserRole.Admin) {
        allCashiers = cashiers.filter(isUserInMyGroupHierarchy);
      }
      filterCashiers();
      cashiersLoaded = true;
      
      if (startDate && endDate && allCashiers.length > 0) {
        await loadAllCashierReports();
      }
    });
    
    const currentCashiers = get(usersByRole[UserRole.Cashier]);
    if (currentCashiers.length === 0) {
      RefreshUsers();
    }
  }

  async function loadCashiersDirectly() {
    try {
      const token = await GetCachedToken();
      if (!token) {
        console.error('No token available for direct cashier loading');
        return;
      }

      const response = await service.walletapi.cashierList({ Token: token });
      if (response.Success && response.Data) {
        
        if ($me?.Role === UserRole.Owner) {
          allCashiers = response.Data;
        } else if ($me?.Role === UserRole.Admin) {
          allCashiers = response.Data.filter(isUserInMyGroupHierarchy);
        }
        
        filterCashiers();
        cashiersLoaded = true;
        
        if (startDate && endDate && allCashiers.length > 0) {
          await loadAllCashierReports();
        }
      }
    } catch (err) {
      console.error('Error loading cashiers directly:', err);
    }
  }

  function isUserInMyGroupHierarchy(user: User): boolean {
    if ($me?.Role === UserRole.Owner) {
      return true;
    }

    if ($me?.Role === UserRole.Admin) {
      if (!user.GroupId || !$me?.GroupId) {
        return false;
      }

      if (user.GroupId === $me.GroupId) {
        return true;
      }

      if (user.Group?.ParentId === $me.GroupId) {
        return true;
      }
      let currentGroup = user.Group;
      while (currentGroup?.ParentId) {
        if (currentGroup.ParentId === $me.GroupId) {
          return true;
        }

        currentGroup = get(groups).find(g => g.Id === currentGroup?.ParentId);
      }

      return false;
    }

    return false;
  }

  function filterCashiers() {
    if (searchInput == '') {
      searchCashiers = allCashiers;
    } else {
      let searchInputNormalized = searchInput.toLowerCase();
      searchCashiers = allCashiers.filter((cashier) =>
        cashier.Email?.toLowerCase().includes(searchInputNormalized) ||
        cashier.FirstName?.toLowerCase().includes(searchInputNormalized) ||
        cashier.LastName?.toLowerCase().includes(searchInputNormalized)
      );
    }
  }

  function getFilteredCashierReports(): CashierReport[] {
    if (searchInput === '') {
      return allCashierReports;
    }
    
    let searchInputNormalized = searchInput.toLowerCase();
    return allCashierReports.filter((cashierReport) => {
      const cashier = cashierReport.cashier;
      return cashier.Email?.toLowerCase().includes(searchInputNormalized) ||
             cashier.FirstName?.toLowerCase().includes(searchInputNormalized) ||
             cashier.LastName?.toLowerCase().includes(searchInputNormalized);
    });
  }

  function clearSearch() {
    searchInput = '';
  }

  function viewCashierDetails(cashier: User | PublicUserDTO) {
    if (!cashier || !cashier.Id) {
      return;
    }
    
    const url = `/details/${cashier.Id}?startDate=${startDate}&endDate=${endDate}`;
    push(url);
    
    setTimeout(() => {
      const currentUrl = window.location.pathname;
      if (!currentUrl.includes('/details/')) {
        const fallbackUrl = `/details?cashierId=${cashier.Id}&startDate=${startDate}&endDate=${endDate}`;
        push(fallbackUrl);
      }
    }, 100);
  }

  function IsSearchAndNoCashiersMatch(searchCashiers: User[]) {
    return searchInput && searchCashiers.length == 0;
  }

  $: {
    if ($me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin) {
      searchInput;
      filterCashiers();
    }
  }
</script>

<div class="ui container">
  <div class="ui segment">
    <h3 class="ui header">
      <i class="chart line icon"></i>
      <div class="content">
        {#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
          Cashier Reports
          <div class="sub header">
            <i class="users icon"></i> Select a cashier to view their transaction report
          </div>
        {:else}
          Cashier Transaction Report
          <div class="sub header">
            <i class="sync icon"></i> Analysis of transactions by cashier
          </div>
        {/if}
      </div>
    </h3>
  </div>

  {#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
      <div class="ui form mobile-form">
        <div class="fields">
          <div class="field">
            <label>Start Date</label>
            <div class="ui input">
              <input type="date" bind:value={startDate} on:change={handleDateChange}>
            </div>
          </div>
          <div class="field">
            <label>End Date</label>
            <div class="ui input">
              <input type="date" bind:value={endDate} on:change={handleDateChange}>
            </div>
          </div>
          <div class="field">
            <label>&nbsp;</label>
            <div class="ui buttons">
              <button 
                class="ui primary button mobile-search-btn"
                on:click={loadAllCashierReports}
                disabled={loadingAllReports}
              >
                {#if loadingAllReports}
                  <i class="spinner loading icon"></i>
                  Loading
                {:else}
                  <i class="search icon"></i>
                  Search
                {/if}
              </button>
            </div>
          </div>
        </div>
      </div>

      <h4 class="ui header">
        <i class="users icon"></i>
        <div class="content">
          All Cashiers Report Summary
          <div class="sub header">View reports for all cashiers</div>
        </div>
      </h4>
      
      <div class="ui action input" style="margin-bottom: 1rem;">
        <input type="text" bind:value={searchInput} placeholder="Search cashiers..." autocomplete="off">
        <button class="ui icon button" on:click={clearSearch}>
          <i class="times icon"></i>
        </button>
      </div>

      {#if loadingAllReports}
        <div class="ui segment">
          <div class="ui active centered inline loader"></div>
          <p style="text-align: center; margin-top: 1rem;">Loading all cashier reports...</p>
        </div>
      {:else if allCashierReports.length > 0}
          <h4 class="ui header">
            <i class="chart bar icon"></i>
            <div class="content">
              All Cashiers Report Summary
              <div class="sub header">
                Period: {formatDateTime(new Date(startDate + 'T00:00:00'))} - {formatDateTime(new Date(endDate + 'T23:59:59'))}
              </div>
            </div>
          </h4>

          <div class="ui table mobile-table-container">
            <div class="table-wrapper">
              <table class="ui celled unstackable table mobile-table">
              <thead>
                <tr>
                  <th class="center aligned">Cashier</th>
                  <th class="center aligned">Group</th>
                  <th class="center aligned">Deposit Total</th>
                  <th class="center aligned">Deposit Promo</th>
                  <th class="center aligned">Withdraw Total</th>
                  <th class="center aligned">Profit</th>
                  <th class="center aligned">Details</th>
                </tr>
              </thead>
              <tbody>
                {#each getFilteredCashierReports() as cashierReport}
                  <tr>
                    <td class="center aligned">
                      <strong>{cashierReport.cashier.FirstName} {cashierReport.cashier.LastName}</strong>
                      <!-- <div class="ui mini label">ID: {cashierReport.cashier.Id}</div> -->
                      <!-- <div class="ui mini label">{cashierReport.cashier.Email}</div> -->
                    </td>
                    <td class="center aligned">{cashierReport.cashier.Group?.Name ?? 'N/A'}</td>
                    {#if cashierReport.loading}
                      <td colspan="5" class="center aligned">
                        <div class="ui active mini inline loader"></div>
                        Loading...
                      </td>
                    {:else if cashierReport.error}
                      <td colspan="5" class="center aligned error">
                        <i class="exclamation triangle icon"></i>
                        {cashierReport.error}
                      </td>
                    {:else if cashierReport.reportData}
                      <td class="positive center aligned">
                        <strong>
                          {#if cashierReport.reportData.TotalDepositMoney}
                            {formatMoney(cashierReport.reportData.TotalDepositMoney)}
                          {:else}
                            $0.00
                          {/if}
                        </strong>
                      </td>
                      <td class="positive center aligned">
                        {#if cashierReport.reportData.TotalDepositPromo}
                          {formatMoney(cashierReport.reportData.TotalDepositPromo)}
                        {:else}
                          $0.00
                        {/if}
                      </td>
                      <td class="negative center aligned">
                        <strong>
                          {#if cashierReport.reportData.TotalWithdrawMoney}
                            {formatMoney(cashierReport.reportData.TotalWithdrawMoney)}
                          {:else}
                            $0.00
                          {/if}
                        </strong>
                      </td>
                      <td class="{((cashierReport.reportData.TotalDepositMoney?.InCents || 0) - (cashierReport.reportData.TotalWithdrawMoney?.InCents || 0)) >= 0 ? 'positive' : 'negative'} center aligned">
                        <strong>
                          {formatMoney((cashierReport.reportData.TotalDepositMoney?.InCents || 0) - (cashierReport.reportData.TotalWithdrawMoney?.InCents || 0))}
                        </strong>
                      </td>
                      <td class="center aligned">
                        <button 
                          class="ui mini primary button"
                          on:click={() => {
                            viewCashierDetails(cashierReport.cashier);
                          }}
                        >
                          <i class="eye icon"></i>
                          Details
                        </button>
                      </td>
                    {:else}
                      <td colspan="5" class="center aligned">
                        <i class="info circle icon"></i>
                        No data available
                      </td>
                    {/if}
                  </tr>
                {/each}
              </tbody>
              </table>
            </div>
          </div>
      {:else if IsSearchAndNoCashiersMatch(searchCashiers)}
        <div class="ui negative message">
          <div class="header">No cashiers found</div>
        </div>
      {:else}
        <div class="ui info message">
          <div class="header">No cashiers available</div>
          <p>There are no cashiers to display reports for.</p>
      </div>
    {/if}
  {:else}
      <div class="ui form mobile-form">
        <div class="fields">
          <div class="field">
            <label>Start Date</label>
            <div class="ui input">
              <input type="date" bind:value={startDate} on:change={handleDateChange}>
            </div>
          </div>
          <div class="field">
            <label>End Date</label>
            <div class="ui input">
              <input type="date" bind:value={endDate} on:change={handleDateChange}>
            </div>
          </div>
          <div class="field">
            <label>&nbsp;</label>
            <div class="ui buttons">
              <button 
                class="ui primary button mobile-search-btn"
                on:click={loadCashierReport}
                disabled={loading}
              >
                {#if loading}
                  <i class="spinner loading icon"></i>
                  Loading
                {:else}
                  <i class="search icon"></i>
                  Search
                {/if}
              </button>
            </div>
          </div>
        </div>
      </div>
  {/if}

  {#if error}
    <div class="ui negative message">
      <i class="close icon"></i>
      <div class="header">
       Error
      </div>
      <p>{error}</p>
    </div>
  {/if}

  {#if reportData && $me?.Role === UserRole.Cashier}
      <h4 class="ui header">
        <i class="chart bar icon"></i>
        <div class="content">
           Report Summary
            <div class="sub header">
              Period: {formatDateTime(new Date(startDate + 'T00:00:00'))} - {formatDateTime(new Date(endDate + 'T23:59:59'))}
                | Cashier: {getOperatorName()}
            </div>
        </div>
      </h4>

        <div class="ui table">
          <table class="ui celled unstackable table">
            <thead>
              <tr>
                <th class="center aligned">Cashier</th>
                <th class="center aligned">Deposit Total</th>
                <th class="center aligned">Deposit Promo</th>
                <th class="center aligned">Withdraw Total</th>
                <th class="center aligned">Profit</th>
                <th class="center aligned">Details</th>
              </tr>
            </thead>
            <tbody>
                <tr>
                  <td class="center aligned">
                    <strong>{getOperatorName()}</strong>
                      <!-- <div class="ui mini green label">Current</div> -->
                  </td>
                <td class="positive center aligned">
                  <strong>
                    {#if reportData.TotalDepositMoney}
                      {formatMoney(reportData.TotalDepositMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </strong>
                </td>
                <td class="positive center aligned">
                  {#if reportData.TotalDepositPromo}
                    {formatMoney(reportData.TotalDepositPromo)}
                  {:else}
                    $0.00
                  {/if}
                </td>
                <td class="negative center aligned">
                  <strong>
                    {#if reportData.TotalWithdrawMoney}
                      {formatMoney(reportData.TotalWithdrawMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </strong>
                </td>
                <td class="{((reportData.TotalDepositMoney?.InCents || 0) - (reportData.TotalWithdrawMoney?.InCents || 0)) >= 0 ? 'positive' : 'negative'} center aligned">
                  <strong>
                    {formatMoney((reportData.TotalDepositMoney?.InCents || 0) - (reportData.TotalWithdrawMoney?.InCents || 0))}
                  </strong>
                </td>
                <td class="center aligned">
                  <button 
                    class="ui mini primary button"
                    on:click={() => {
                      if ($me?.Id) {
                        viewCashierDetails($me);
                      }
                    }}
                  >
                    <i class="eye icon"></i>
                    Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
  {/if}

  {#if !reportData && !loading && !error && $me?.Role === UserRole.Cashier}
      <div class="ui center aligned basic segment">
        <i class="chart bar icon" style="font-size: 4em; color: #ccc;"></i>
        <h3 class="ui header">
          No hay datos para mostrar
          <div class="sub header">
            Selecciona un rango de fechas y haz clic en "Buscar" para generar el reporte.
          </div>
        </h3>
        <div class="ui buttons">
          <button 
            class="ui primary button"
            on:click={loadCashierReport}
            disabled={!startDate || !endDate}
          >
            <i class="search icon"></i>
            Generar Reporte
          </button>
        </div>
      </div>
  {/if}
</div>

<style>
  /* Estilos específicos para formularios móviles */
  .mobile-form {
    margin-bottom: 1rem;
  }
  
  .mobile-search-btn {
    min-height: 44px !important;
    font-size: 16px !important;
  }
  
  /* Contenedor de tabla móvil */
  .mobile-table-container {
    position: relative;
  }
  
  
  .table-wrapper {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    border: 1px solid #ddd !important;
    border-radius: 4px !important;
    max-width: 100% !important;
  }
  
  .mobile-table {
    min-width: 100% !important;
    width: max-content !important;
    margin: 0 !important;
  }
  
  /* Estilos para desktop - tabla completa sin scroll */
  @media screen and (min-width: 769px) {
    .table-wrapper {
      overflow-x: visible !important;
    }
    
    .mobile-table {
      width: 100% !important;
      min-width: auto !important;
    }
  }
  
  /* Estilos específicos para que la tabla se vea como en la imagen */
  .ui.celled.table {
    border-collapse: separate !important;
    border-spacing: 0 !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
  }
  
  .ui.celled.table th,
  .ui.celled.table td {
    border-left: 1px solid #d4d4d5 !important;
    border-top: 1px solid #d4d4d5 !important;
    padding: 0.78571429em 0.78571429em !important;
    text-align: center !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    font-size: 0.9rem !important;
  }
  
  .ui.celled.table th:first-child,
  .ui.celled.table td:first-child {
    border-left: none !important;
  }
  
  .ui.celled.table thead th {
    background: #f9fafb !important;
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    font-size: 0.9rem !important;
  }
  
  /* Colores para las celdas */
  .positive {
    color: #21ba45 !important;
    font-weight: 500 !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
  }
  
  .negative {
    color: #db2828 !important;
    font-weight: 500 !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
  }
  
  /* Botón Details */
  .ui.mini.primary.button {
    background-color: #2185d0 !important;
    color: white !important;
    border-radius: 0.28571429rem !important;
    padding: 0.4em 0.8em !important;
    font-size: 0.8rem !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    font-weight: 500 !important;
  }
  
  /* Label Current
  .ui.mini.green.label {
    background-color: #21ba45 !important;
    color: white !important;
    font-size: 0.7rem !important;
    padding: 0.3em 0.6em !important;
    border-radius: 0.28571429rem !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    font-weight: 500 !important;
  } */
  
  /* Cashier names and labels standardization */
  .ui.celled.table tbody td strong {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    font-weight: 600 !important;
    font-size: 0.9rem !important;
    display: block !important;
    text-align: center !important;
  }
  
  /* .ui.mini.label {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
    font-weight: 500 !important;
    font-size: 0.7rem !important;
    display: inline-block !important;
    margin: 0.2rem 0.1rem !important;
  } */
  
  /* Center all content within table cells */
  .ui.celled.table tbody td {
    text-align: center !important;
    vertical-align: middle !important;
  }
  
  /* Center cashier info specifically */
  .ui.celled.table tbody td:first-child {
    text-align: center !important;
  }
  
  .ui.celled.table tbody td:first-child > div {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 0.2rem !important;
  }
  
  /* Estilos para responsividad móvil */
  @media screen and (max-width: 768px) {
    .ui.container {
      padding: 0.5rem !important;
    }
    
    /* Formularios responsivos */
    .ui.form .fields {
      flex-direction: column !important;
    }
    
    .ui.form .fields .field {
      margin-bottom: 1rem !important;
    }
    
    .ui.form .fields .field:last-child {
      margin-bottom: 0 !important;
    }
    
    /* Botones de búsqueda */
    .ui.buttons {
      width: 100% !important;
    }
    
    .ui.primary.button {
      width: 100% !important;
      margin: 0 !important;
    }
    
    /* Tabla responsiva */
    .ui.table {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
    }
    
    .ui.table table {
      min-width: 900px !important;
    }
    
    /* Tabla móvil con scroll horizontal */
    .table-wrapper {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
      border: 1px solid #ddd !important;
      border-radius: 4px !important;
      /* Scrollbar personalizada */
      scrollbar-width: thin !important;
      scrollbar-color: #c1c1c1 #f1f1f1 !important;
    }
    
    .table-wrapper::-webkit-scrollbar {
      height: 8px !important;
    }
    
    .table-wrapper::-webkit-scrollbar-track {
      background: #f1f1f1 !important;
      border-radius: 4px !important;
    }
    
    .table-wrapper::-webkit-scrollbar-thumb {
      background: #c1c1c1 !important;
      border-radius: 4px !important;
    }
    
    .table-wrapper::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8 !important;
    }
    
    .mobile-table {
      min-width: 900px !important;
      width: max-content !important;
    }
    
    /* Headers más compactos */
    .ui.header {
      font-size: 1.2rem !important;
    }
    
    .ui.header .sub.header {
      font-size: 0.9rem !important;
    }
    
    /* Input de búsqueda */
    .ui.action.input {
      width: 100% !important;
    }
    
    .ui.action.input input {
      width: calc(100% - 40px) !important;
    }
    
    /* Cards de cashier más compactos */
    .ui.table tbody td {
      padding: 0.5rem !important;
      font-size: 0.9rem !important;
      text-align: center !important;
      vertical-align: middle !important;
    }
    
    /* Labels más pequeños */
    /* .ui.mini.label {
      font-size: 0.7rem !important;
      padding: 0.3rem 0.5rem !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      font-weight: 500 !important;
    } */
    
    /* Botones más pequeños */
    .ui.mini.button {
      font-size: 0.8rem !important;
      padding: 0.4rem 0.6rem !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
      font-weight: 500 !important;
    }
    
    /* Mensajes de error más compactos */
    .ui.message {
      margin: 0.5rem 0 !important;
      padding: 0.8rem !important;
    }
    
    /* Segmentos más compactos */
    .ui.segment {
      padding: 1rem !important;
      margin: 0.5rem 0 !important;
    }
    
  }
  
  /* Estilos para pantallas muy pequeñas */
  @media screen and (max-width: 480px) {
    .ui.container {
      padding: 0.25rem !important;
    }
    
    /* Headers aún más pequeños */
    .ui.header {
      font-size: 1rem !important;
      margin: 0.5rem 0 !important;
    }
    
    /* Tabla con scroll horizontal mejorado */
    .ui.table {
      border: none !important;
    }
    
    .ui.table table {
      border: 1px solid #ddd !important;
      border-radius: 4px !important;
    }
    
    /* Botones de acción más grandes para touch */
    .ui.mini.button {
      min-height: 36px !important;
      padding: 0.6rem 1rem !important;
    }
    
    /* Inputs más grandes para touch */
    .ui.input input {
      min-height: 44px !important;
      font-size: 16px !important; /* Previene zoom en iOS */
    }
    
    /* Labels más legibles */
    .ui.form label {
      font-size: 0.9rem !important;
      font-weight: 600 !important;
    }
  }
  
  /* Estilos para orientación landscape en móviles */
  @media screen and (max-width: 768px) and (orientation: landscape) {
    .ui.form .fields {
      flex-direction: row !important;
      flex-wrap: wrap !important;
    }
    
    .ui.form .fields .field {
      flex: 1 1 200px !important;
      margin-right: 1rem !important;
    }
    
    .ui.form .fields .field:last-child {
      flex: 0 0 auto !important;
      margin-right: 0 !important;
    }
  }
  
  /* Mejoras de accesibilidad */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
  
</style>
